// PublicSafetyDashboard.tsx
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useState, useMemo } from 'react';
import { useEvents } from './lib/useEvents';
import './PublicSafetyDashboard.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const WINDOWS = [
  { label: 'Next 24h', value: '24h' as const },
  { label: 'Next 48h', value: '48h' as const },
  { label: 'Next 72h', value: '72h' as const },
  { label: 'Next 7 days', value: '7d' as const },
];

const MAP_CENTER: LatLngExpression = [39.8, -98.5];

export function PublicSafetyDashboard() {
  const [windowSize, setWindowSize] = useState<'24h' | '48h' | '72h' | '7d'>('72h');
  const { events, loading, error } = useEvents(windowSize);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Only plot events with valid lat/lng
  const mappableEvents = useMemo(
      () => events.filter(ev => typeof ev.latitude === 'number' && typeof ev.longitude === 'number'),
      [events]
  );

  const selectedEvent = mappableEvents.find(ev => ev.id === selectedId) || null;

  return (
      <div className="dashboard">
        {/* Top Bar */}
        <header className="top-bar">
          <div className="top-bar-left">
            <h1>Public Safety Dashboard</h1>
            <p className="subtitle">AI-powered feed from Google Alerts</p>
          </div>
          <div className="top-bar-right">
            <div className="window-toggle">
              {WINDOWS.map(w => (
                  <button
                      key={w.value}
                      onClick={() => setWindowSize(w.value)}
                      className={w.value === windowSize ? 'pill active' : 'pill'}
                  >
                    {w.label}
                  </button>
              ))}
            </div>
            <div className="status">
            <span className="status-count">
              {loading ? 'Loading…' : `Events: ${events.length}`}
            </span>
              {error && <span className="status-error">Error loading events</span>}
            </div>
          </div>
        </header>

        {/* Metrics row (simple, can grow later) */}
        <section className="metrics-row">
          <div className="metric-card">
            <div className="metric-label">Active window</div>
            <div className="metric-value">
              {WINDOWS.find(w => w.value === windowSize)?.label}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Mapped events</div>
            <div className="metric-value">{mappableEvents.length}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Unique locations</div>
            <div className="metric-value">
              {
                new Set(
                    mappableEvents
                        .map(ev => [ev.city, ev.state].filter(Boolean).join(', '))
                        .filter(Boolean)
                ).size
              }
            </div>
          </div>
        </section>

        {/* Main layout */}
        <div className="layout">
          {/* Left list */}
          <aside className="list-panel">
            <div className="panel-header">
              <h2>Events</h2>
            </div>
            <div className="list-scroll">
              {mappableEvents.length === 0 && !loading && (
                  <div className="empty-state">
                    No events in this time window.
                  </div>
              )}
              {mappableEvents.map(ev => (
                  <div
                      key={ev.id}
                      className={
                          'list-item' + (ev.id === selectedId ? ' selected' : '')
                      }
                      onClick={() => setSelectedId(ev.id)}
                  >
                    <div className="list-item-title">{ev.event_title}</div>
                    <div className="list-item-time">
                      {new Date(ev.event_start).toLocaleString()}
                    </div>
                    <div className="list-item-location">
                      {[ev.specific_place, ev.city, ev.state]
                          .filter(Boolean)
                          .join(', ')}
                    </div>
                  </div>
              ))}
            </div>
          </aside>

          {/* Map */}
          <section className="map-panel">
            <div className="panel-header">
              <h2>Map</h2>
            </div>
            <div className="map-wrapper">
              <MapContainer
                  center={MAP_CENTER}
                  zoom={4}
                  minZoom={3}
                  className="map-container"
                  scrollWheelZoom={true}
              >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                />
                {mappableEvents.map(ev => (
                    <Marker
                        key={ev.id}
                        position={[ev.latitude, ev.longitude]}
                        eventHandlers={{ click: () => setSelectedId(ev.id) }}
                    >
                      <Popup>
                        <strong>{ev.event_title}</strong>
                        <br />
                        {new Date(ev.event_start).toLocaleString()}
                        <br />
                        {[ev.specific_place, ev.city, ev.state]
                            .filter(Boolean)
                            .join(', ')}
                        <br />
                        <small>{ev.event_summary}</small>
                        {ev.rss_link && (
                            <>
                              <br />
                              <a href={ev.rss_link} target="_blank" rel="noreferrer">
                                Source
                              </a>
                            </>
                        )}
                      </Popup>
                    </Marker>
                ))}
              </MapContainer>
            </div>
          </section>

          {/* Right sidebar (detail / future charts) */}
          <aside className="detail-panel">
            <div className="panel-header">
              <h2>Details</h2>
            </div>
            <div className="detail-body">
              {selectedEvent ? (
                  <>
                    <h3>{selectedEvent.event_title}</h3>
                    <p className="detail-time">
                      {new Date(selectedEvent.event_start).toLocaleString()}
                    </p>
                    <p className="detail-location">
                      {[selectedEvent.specific_place, selectedEvent.city, selectedEvent.state]
                          .filter(Boolean)
                          .join(', ')}
                    </p>
                    <p className="detail-summary">{selectedEvent.event_summary}</p>
                    {selectedEvent.rss_link && (
                        <p>
                          <a
                              href={selectedEvent.rss_link}
                              target="_blank"
                              rel="noreferrer"
                          >
                            View source article
                          </a>
                        </p>
                    )}
                  </>
              ) : (
                  <div className="empty-state">
                    Select an event from the list or map to see details.
                  </div>
              )}
            </div>
          </aside>
        </div>
      </div>
  );
}

export default PublicSafetyDashboard;
